/**
 * 工时记录面板组件
 */
import React, { useState, useEffect } from 'react';
import { Table, Spin, Empty, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { WorkTimeService } from '../../../services/workTimeService';
import { formatMoney } from '../../../utils/budgetUtils';

interface WorkTimePanelProps {
  projectId: string;
  isMobile?: boolean;
  active?: boolean;
}

interface WorkTimeRecord {
  _id: string;
  workDate: string;
  workerType: string;
  workerName?: string;
  workUnits?: number;
  timeUnit?: string;
  totalCost?: number;
  workContent?: string;
}

const WorkTimePanel: React.FC<WorkTimePanelProps> = ({
  projectId,
  isMobile = false,
  active = false
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WorkTimeRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: isMobile ? 5 : 10,
    total: 0
  });
  const [loaded, setLoaded] = useState(false);

  const fetchData = async (page = 1, pageSize = isMobile ? 5 : 10) => {
    if (!projectId) return;

    setLoading(true);
    try {
      const response = await WorkTimeService.getWorkTimes({
        projectId,
        page,
        limit: pageSize
      });

      if (response.success && response.data) {
        setData(response.data);
        setPagination({
          current: response.pagination?.page || page,
          pageSize: response.pagination?.limit || pageSize,
          total: response.pagination?.total || 0
        });
      }
    } catch (error) {
      console.error('获取工时记录失败:', error);
      message.error('获取工时记录失败');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  const getWorkerLabel = (record: WorkTimeRecord) => {
    const workerTypeText = record.workerType === 'male' ? '男工' : '女工';
    return record.workerName ? `${workerTypeText} · ${record.workerName}` : workerTypeText;
  };

  const getWorkUnitsText = (record: WorkTimeRecord) => {
    const units = record.workUnits || (record.timeUnit === 'half' ? 0.5 : 1);
    return `${units}工`;
  };

  const columns: ColumnsType<WorkTimeRecord> = isMobile ? [
    {
      title: '人员/日期',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{getWorkerLabel(record)}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {formatDate(record.workDate)} · {getWorkUnitsText(record)}
          </div>
          {record.workContent && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
              {record.workContent}
            </div>
          )}
        </div>
      )
    },
    {
      title: '金额',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 100,
      align: 'right',
      render: (val: number) => formatMoney(val || 0, false)
    }
  ] : [
    {
      title: '日期',
      dataIndex: 'workDate',
      key: 'workDate',
      width: 120,
      render: (val: string) => formatDate(val)
    },
    {
      title: '人员',
      key: 'worker',
      width: 180,
      render: (_, record) => getWorkerLabel(record)
    },
    {
      title: '工数',
      key: 'workUnits',
      width: 120,
      render: (_, record) => getWorkUnitsText(record)
    },
    {
      title: '金额',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 130,
      align: 'right',
      render: (val: number) => formatMoney(val || 0, false)
    },
    {
      title: '备注',
      dataIndex: 'workContent',
      key: 'workContent',
      ellipsis: true,
      render: (val: string) => val || '-'
    }
  ];

  if (!loaded && !active) {
    return null;
  }

  if (loading && !data.length) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin tip="加载中..." />
      </div>
    );
  }

  if (!data.length) {
    return <Empty description="暂无工时记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="_id"
      loading={loading}
      size="small"
      pagination={{
        ...pagination,
        size: 'small',
        showSizeChanger: false,
        showTotal: (total) => `共 ${total} 条`,
        onChange: (page, pageSize) => fetchData(page, pageSize)
      }}
    />
  );
};

export default WorkTimePanel;
